import type {
  DesignarAsesorInput,
  EmitirConformidadProyectoInput,
  ObservarProyectoInput,
} from "@copi/shared";

import { apiClient } from "./client";
import type { ExpedienteWire } from "./expedientes";
import { unwrap } from "./unwrap";

export interface AsesorDesignadoWire {
  id: string;
  expedienteId: string;
  asesorId: string;
  resolucionId: string;
  fechaInicio: string;
  fechaFin: string | null;
}

export interface AsesoriaActivaWire {
  asesorDesignadoId: string;
  expediente: ExpedienteWire;
  fechaDesignacion: string;
}

/**
 * El backend acepta `fechaResolucion: Date` vía `z.coerce.date()`. Por la red
 * la enviamos como ISO string — Zod la convierte.
 */
type DesignarAsesorWire = Omit<DesignarAsesorInput, "fechaResolucion"> & {
  fechaResolucion: string;
};

export const asesoriaApi = {
  designar(expedienteId: string, input: DesignarAsesorInput): Promise<AsesorDesignadoWire> {
    const wire: DesignarAsesorWire = {
      asesorId: input.asesorId,
      numeroResolucion: input.numeroResolucion,
      fechaResolucion: input.fechaResolucion.toISOString(),
    };
    return apiClient
      .post(`/expedientes/${expedienteId}/asesor`, wire, { idempotent: true })
      .then(unwrap<AsesorDesignadoWire>);
  },

  /** Lista las asesorías activas (`fechaFin = null`) del docente autenticado. */
  misEstudiantes(): Promise<AsesoriaActivaWire[]> {
    return apiClient.get("/asesoria/mis-estudiantes").then(unwrap<AsesoriaActivaWire[]>);
  },

  /**
   * Emite la conformidad del proyecto. Requiere que el documento
   * INFORME_CONFORMIDAD_ASESOR ya esté subido — el caller orquesta el upload
   * antes de invocar este método.
   */
  emitirConformidad(
    expedienteId: string,
    input: EmitirConformidadProyectoInput,
  ): Promise<{ ok: true }> {
    return apiClient
      .post(`/expedientes/${expedienteId}/conformidad-proyecto`, input, { idempotent: true })
      .then(unwrap<{ ok: true }>);
  },

  /**
   * Estudiante envía el proyecto a revisión del asesor (ADR 019).
   * Transita PROYECTO_EN_ELABORACION → PROYECTO_EN_REVISION_ASESOR.
   */
  enviarARevisionAsesor(expedienteId: string): Promise<{ ok: true }> {
    return apiClient
      .post(`/expedientes/${expedienteId}/enviar-a-revision-asesor`, {}, { idempotent: true })
      .then(unwrap<{ ok: true }>);
  },

  /**
   * Asesor devuelve el proyecto al estudiante con observaciones (ADR 019).
   * Transita PROYECTO_EN_REVISION_ASESOR → PROYECTO_EN_ELABORACION.
   */
  observarProyecto(expedienteId: string, input: ObservarProyectoInput): Promise<{ ok: true }> {
    return apiClient
      .post(`/expedientes/${expedienteId}/observar-proyecto`, input, { idempotent: true })
      .then(unwrap<{ ok: true }>);
  },
};
